import {
  ArrowDownTrayIcon,
  CheckCircleIcon,
  CheckIcon,
  DocumentMagnifyingGlassIcon,
  QuestionMarkCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { CreateCloudPolicyRequest } from "components/CloudPolicy/types";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "components/Graphs/Accordion";
import { Input } from "components/Graphs/Input";
import { ProgressBar } from "components/Graphs/ProgressBar";
import { Button, CopyButton } from "components/UI-lib";
import Papa from "papaparse";
import React, { useEffect, useRef, useState } from "react";
import { Prompt } from "react-router-dom";
import agent from "server";
import { buildInviteUrl, sleep } from "server/utils";

type BulkCreateRequest = {
  credits: string;
  gpuHours: string;
  labels: Record<string, string>;
  originalHeader: string[];
  originalRow: OrgCsvRow;
  orgName: string;
  policies: CreateCloudPolicyRequest[];
  programId: string | undefined;
  resourceLimit: string | undefined;
};
type BulkCreateResult = {
  message: string;
  status: "complete" | "error" | "starting" | "success";
};
type ExportedResult = {
  invite_link: string;
  org_id: string;
  org_slug: string;
  request: BulkCreateRequest;
};
type OrgCsvRow = {
  credits_usd: string;
  gpu_hours: string;
  labels?: string;
  org_name: string;
  program_id?: string;
  resource_limit_usd?: string;
} & {
  [K in `policy-${string}`]?: string; // may contain n-number of policy definitions
};

const BulkCreate: React.FC = () => {
  const batchInterval = 250; // Time between API request batches in milliseconds
  const batchSize = 5; // Number of API requests to send in parallel
  const policyPrefix = "policy-";
  const orgWidePolicyColumn = `${policyPrefix}orgWide`;

  const completedRequests = useRef<number>(0);
  const createQueue = useRef<BulkCreateRequest[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const resultsContainerRef = useRef<HTMLDivElement>(null);
  const [createdCount, setCreatedCount] = useState<number>(0);
  const [csvFile, setCsvFile] = useState<File | undefined>();
  const [errorCount, setErrorCount] = useState<number>(0);
  const exportedResults = useRef<ExportedResult[]>([]);
  const priceCache = useRef<Record<string, number>>({});
  const [progress, setProgress] = useState<number>(0);
  const [results, setResults] = useState<BulkCreateResult[]>([]);
  const requestCount = useRef<number>(0);
  const [userHasScrolled, setUserHasScrolled] = useState(false);

  useEffect(() => {
    // Prompt the user before closing the window if there are pending requests
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (progress > 0 && progress < 100) {
        event.preventDefault();
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [progress]);

  useEffect(() => {
    // Auto-scroll to bottom of results until user manually scrolls
    if (!userHasScrolled && resultsContainerRef.current && results.length > 0) {
      resultsContainerRef.current.scrollTop =
        resultsContainerRef.current.scrollHeight;
    }
  }, [results, userHasScrolled]);

  const awardCredits = async (orgId: string, credits: string) => {
    const creditRes = await agent.Admin.addCreditsToOrganization({
      amountUSD: credits,
      organizationId: orgId,
    });
    if (!creditRes.success) {
      throw new Error(
        creditRes.message || "An unexpected error occurred when adding credits"
      );
    }
  };

  const setResourceLimits = async (orgId: string, resourceLimit: string) => {
    const resourceLimitRes = await agent.Admin.setResourceLimits(
      orgId,
      Number(resourceLimit) * 100 // Convert to cents
    );
    if (!resourceLimitRes.success) {
      throw new Error(
        resourceLimitRes.message || "An unexpected error occurred when setting resource limits"
      );
    }
  };

  const cacheInstancePrices = async () => {
    if (Object.keys(priceCache.current).length > 0) {
      return;
    }

    // Cache instance prices in case we need to convert GPU hours to credits
    const res = await agent.Instances.getAllPublicInstanceTypes();
    if (!res.success || !res.data) {
      console.error("Failed to fetch instance types", res);
      return;
    }
    res.data.allInstanceTypes.forEach((instanceType) => {
      priceCache.current[instanceType.type] = Number(
        instanceType.base_price?.amount || 0
      );
    });
  };

  const convertHoursToCredits = (
    hours: string,
    policies: CreateCloudPolicyRequest[]
  ) => {
    const baseError = "Unable to convert GPU hours to credits";
    // Find the first instance_type defined in the given cloud policies to determine a base price
    const instanceType = policies
      .find((policy) => policy.quotas?.find((quota) => quota.instance_type))
      ?.quotas?.find((quota) => quota.instance_type)?.instance_type;

    if (!instanceType) {
      throw new Error(
        `${baseError}: At least one instance_type quota is required`
      );
    }
    if (!priceCache.current[instanceType]) {
      throw new Error(
        `${baseError}: Couldn't determine base price of ${instanceType}`
      );
    }

    return (Number(hours) * priceCache.current[instanceType]).toFixed(2);
  };

  const createPolicies = async (
    orgId: string,
    policies: CreateCloudPolicyRequest[]
  ) => {
    const policyRes = await Promise.all(
      policies.map((policy) =>
        agent.Admin.createCloudPolicy(orgId!, {
          ...policy,
          organization_id: orgId,
        })
      )
    );
    if (!policyRes.every((res) => res.success)) {
      throw new Error("Failed to apply policies");
    }
  };

  const loadFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCsvFile(e.target.files?.[0]);
  };

  const getInviteLink = async (orgId: string): Promise<string | undefined> => {
    try {
      const inviteRes = await agent.Organizations.createInviteLink(orgId);
      return buildInviteUrl(orgId, inviteRes.data);
    } catch (error) {
      console.error(error);
    }
  };

  const parsePolicies = (row: OrgCsvRow) => {
    const policies: CreateCloudPolicyRequest[] = [];
    for (const column in row) {
      if (column.startsWith(policyPrefix) && row[column]) {
        const parsed = new URLSearchParams(row[column]);

        let name: string;
        let rule: string;
        if (column === orgWidePolicyColumn) {
          name = "org-wide-default-rule-auto-generated";
          rule = row[orgWidePolicyColumn] || "allow";
        } else {
          name = column;
          rule = parsed.get("rule") || "allow";
        }

        policies.push({
          cloud_cred_id: parsed.get("provider") || undefined,
          context: {
            description: parsed.get("description") || "",
            name,
          },
          default_rule:
            rule === "deny" ? "DEFAULT_RULE_DENY" : "DEFAULT_RULE_ALLOW",
          quotas: parsed.getAll("quota").map((quota) => {
            const [key, value, limit] = quota.split(":");
            if (!key || !value || !limit) {
              throw new Error(`Invalid quota definition: ${quota}`);
            }
            return {
              [key]: value,
              limit: parseInt(limit),
            };
          }),
        });
      }
    }
    return policies;
  };

  const processCsvFile = async () => {
    reset();
    setResults([{ message: "Processing CSV file", status: "starting" }]);

    await cacheInstancePrices();

    if (csvFile) {
      Papa.parse(csvFile, {
        chunk: processRows,
        complete: processQueue,
        delimiter: ",",
        header: true,
        skipEmptyLines: true,
      });
    }
  };

  const processQueue = async () => {
    for (let i = 0; i < createQueue.current.length; i += batchSize) {
      await Promise.all(
        createQueue.current.slice(i, i + batchSize).map(processRequest)
      );
      if (i < createQueue.current.length - batchSize) {
        await sleep(batchInterval);
      }
    }
    setProgress(100);
    setResults((prevResults) => [
      ...prevResults,
      { message: "Processing complete", status: "complete" },
    ]);
  };

  const processRequest = async (request: BulkCreateRequest) => {
    let error;
    let orgId: string | undefined;

    try {
      const orgRes = await agent.Organizations.createOrSyncOrg(
        request.orgName,
        undefined,
        true,
        request.labels,
        request.programId
      );
      orgId = orgRes.data?.id;

      if (!orgRes.success) {
        throw new Error(orgRes.message || "An unexpected error occurred");
      }

      if (orgId) {
        const inviteLink = await getInviteLink(orgId);
        exportedResults.current.push({
          invite_link: inviteLink || "Failed to generate invite link",
          org_id: orgId,
          org_slug: orgRes.data?.slug || "",
          request,
        });

        if (request.credits) {
          await awardCredits(orgId, request.credits);
        }

        if (request.resourceLimit) {
          await setResourceLimits(orgId, request.resourceLimit);
        }

        if (request.policies.length) {
          await createPolicies(orgId, request.policies);
        }
      }
    } catch (err: unknown) {
      error = err;
    }

    reportResult(request, orgId, error);
  };

  const processRows = ({ data, meta }: { data: OrgCsvRow[], meta: Papa.Meta }) => {
    data.forEach((row) => {
      // Academic Grant prefers to use Project Title
      const orgName = row.org_name || row['Project Title'];
      if (!orgName) {
        return;
      }

      try {
        const policies = parsePolicies(row);

        let credits = row.credits_usd;
        const gpuHours = row.gpu_hours || row['GPU Hours'];
        if (gpuHours) {
          credits = convertHoursToCredits(gpuHours, policies);
        }

        const labels = Object.fromEntries(new URLSearchParams(row.labels));
        // Add additional labels from other CSV columns
        for (const [key, value] of Object.entries(row)) {
          // Skip excluded keys and policy columns
          if (
            ![
              "org_name",
              "credits_usd",
              "gpu_hours",
              "labels",
              "Project Title",
              "program_id",
              "resource_limit_usd",
            ].includes(key) &&
            !key.startsWith(policyPrefix) &&
            value
          ) {
            labels[key] = String(value);
          }
        }

        createQueue.current.push({
          credits,
          gpuHours,
          labels,
          originalHeader: meta.fields,
          originalRow: row,
          orgName,
          policies,
          programId: row.program_id,
          resourceLimit: row.resource_limit_usd,
        });
      } catch (error) {
        setResults((prevResults) => [
          ...prevResults,
          {
            message: `Failed to parse row for ${orgName} (${error})`,
            status: "error",
          },
        ]);
        setErrorCount((count) => count + 1);
        return;
      }

      requestCount.current++;
    });
  };

  const reportResult = (
    request: BulkCreateRequest,
    orgId: string | undefined,
    error?: unknown
  ) => {
    let message = `Created organization ${request.orgName} (${orgId})`;
    if (error) {
      message = `Failed to create organization ${request.orgName} (${error})`;
    } else {
      const details: string[] = [];
      
      if (request.gpuHours) {
        details.push(`${request.gpuHours} GPU hours`);
      } else if (request.credits) {
        details.push(`${Number(request.credits).toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
        })} of credits`);
      }
      
      if (request.resourceLimit) {
        details.push(`${Number(request.resourceLimit).toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
        })}/hour resource limit`);
      }
      
      if (details.length > 0) {
        message += ` with ${details.join(", ")}`;
      }
    }

    setResults((prevResults) => [
      ...prevResults,
      { message, status: error ? "error" : "success" },
    ]);
    completedRequests.current++;
    error
      ? setErrorCount((count) => count + 1)
      : setCreatedCount((count) => count + 1);
    setProgress(() => (completedRequests.current / requestCount.current) * 100);
  };

  const reset = () => {
    createQueue.current = [];
    completedRequests.current = 0;
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    exportedResults.current = [];
    setCreatedCount(0);
    setCsvFile(undefined);
    setErrorCount(0);
    setProgress(1);
    setResults([]);
    requestCount.current = 0;
    setUserHasScrolled(false);
  };

  const onResultsScroll = () => {
    if (resultsContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } =
        resultsContainerRef.current;
      if (scrollTop + clientHeight !== scrollHeight) {
        setUserHasScrolled(true);
      } else {
        setUserHasScrolled(false);
      }
    }
  };

  const exportResultsAsCsv = (ev: React.MouseEvent<HTMLAnchorElement>) => {
    if (exportedResults.current.length === 0) {
      return;
    }

    ev.preventDefault(); // Prevent click from closing the accordion

    // Write export data to CSV
    const headers = [
      ...exportedResults.current[0].request.originalHeader,
      "org_id",
      "org_slug",
      "invite_link",
    ];
    const csvData = [
      headers.join(","),
      ...exportedResults.current.map((result) =>
        [
          ...Object.values(result.request.originalRow),
          result.org_id,
          result.org_slug,
          result.invite_link,
        ].join(",")
      ),
    ].join("\n");

    // Trigger a download
    const blob = new Blob([csvData], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = `created-orgs-${
      new Date().toISOString().split("T")[0]
    }.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);
  };

  return (
    <div className="flex flex-col">
      {/* Prompt the user before navigating away if there are pending requests */}
      <Prompt
        when={progress > 0 && progress < 100}
        message="Are you sure you want to leave? Pending bulk create operations will be cancelled."
      />
      <div className="w-1/2">
        <div className="flex flex-row items-start justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">
              Bulk Create Organizations
            </h2>
            <p className="text-sm text-secondary mb-5">
              Onboard multiple organizations by uploading a CSV file
            </p>
          </div>
          <div className="flex flex-col gap-1 mt-1">
            <a href="/bulk_org_create.csv">
              <span className="text-highlight text-sm underline flex flex-row items-center gap-1">
                <ArrowDownTrayIcon className="w-4 h-4" />
                CSV Template
              </span>
            </a>
            <a
              href="https://docs.google.com/document/d/1ZAGNEZ0bR2wUFXmuN5wOVIKZH3DJSNPl5gWOEXko89A"
              target="_blank"
            >
              <span className="text-highlight text-sm underline flex flex-row items-center gap-1">
                <QuestionMarkCircleIcon className="w-4 h-4" />
                Usage Guide
              </span>
            </a>
          </div>
        </div>
        <div className="flex flex-col">
          <div className="flex flex-row gap-6">
            <Input
              accept=".csv,.txt"
              ref={fileInputRef}
              type="file"
              onChange={loadFile}
            />
            <Button
              label="Bulk Create"
              className="h-10 whitespace-nowrap"
              disabled={!csvFile}
              onClick={processCsvFile}
            />
          </div>
        </div>
      </div>
      {results.length && (
        <Accordion
          className="w-full mt-4"
          collapsible
          defaultValue="results"
          type="single"
        >
          <AccordionItem value="results">
            <AccordionTrigger>
              <div className="flex flex-col w-full">
                <span>Bulk Create Status</span>
                <div className="flex flex-row gap-4 items-center">
                  <ProgressBar
                    className="h-1.5 w-1/3"
                    showAnimation={true}
                    value={progress}
                    variant="nvidia"
                  />
                  {progress === 100 && exportedResults.current.length > 0 && (
                    <a href="#" onClick={exportResultsAsCsv}>
                      <span className="text-highlight text-sm underline flex flex-row items-center gap-1">
                        <ArrowDownTrayIcon className="w-4 h-4" />
                        Export Results
                      </span>
                    </a>
                  )}
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div
                className="flex flex-row justify-between items-start bg-primaryBg border border-zinc-800 max-h-[500px] overflow-y-auto p-2 rounded-md w-full"
                ref={resultsContainerRef}
                onScroll={onResultsScroll}
              >
                <div className="space-y-2">
                  {results.map((result) => (
                    <div
                      className="flex items-center gap-1"
                      key={result.message}
                    >
                      {result.status === "starting" && (
                        <DocumentMagnifyingGlassIcon className="w-4 h-4" />
                      )}
                      {result.status === "success" && (
                        <CheckCircleIcon className="w-4 h-4 text-green-500" />
                      )}
                      {result.status === "error" && (
                        <XCircleIcon className="w-4 h-4 text-red-500" />
                      )}
                      {result.status === "complete" && (
                        <CheckIcon className="w-4 h-4" />
                      )}
                      <span>{result.message}</span>
                      {result.status === "complete" && (
                        <div className="text-xs text-secondary">
                          <span>(Created: {createdCount}</span>
                          {errorCount > 0 && (
                            <span> / Errors: {errorCount}</span>
                          )}
                          <span>)</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <CopyButton
                  className="sticky top-0"
                  displayValue=" "
                  value={results.map((result) => result.message).join("\n")}
                />
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}
    </div>
  );
};

export default BulkCreate;
